/**
 * VendorTermService
 * Service for managing vendor terms (triple composite key)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for vendor term operations with triple composite key (vendorId + termPeriodTypeId + locationId)
 */
export class VendorTermService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorTermService.DEFAULT_SERVICE_PATH,
            basePath: VendorTermService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all vendor terms
     * @returns Array of vendor terms
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor terms with full response
     * @returns ResponseData with array of vendor terms
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE ID ====================
    /**
     * Get vendor term by triple composite key
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @returns The vendor term
     */
    async getById(vendorId, termPeriodTypeId, locationId) {
        const response = await this.getByIdRaw(vendorId, termPeriodTypeId, locationId);
        if (!response.data) {
            throw new Error(`VendorTerm not found: vendorId=${vendorId}, termPeriodTypeId=${termPeriodTypeId}, locationId=${locationId}`);
        }
        return response.data;
    }
    /**
     * Get vendor term by triple composite key with full response
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @returns ResponseData with vendor term
     */
    getByIdRaw(vendorId, termPeriodTypeId, locationId) {
        const url = this.buildUrl(`${vendorId}/${termPeriodTypeId}/${locationId}`);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new vendor term
     * @param data - Vendor term data
     * @returns The created vendor term
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create VendorTerm');
        }
        return response.data;
    }
    /**
     * Create a new vendor term with full response
     * @param data - Vendor term data
     * @returns ResponseData with created vendor term
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update a vendor term
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @param data - Updated vendor term data
     * @returns The updated vendor term
     */
    async update(vendorId, termPeriodTypeId, locationId, data) {
        const response = await this.updateRaw(vendorId, termPeriodTypeId, locationId, data);
        if (!response.data) {
            throw new Error(`Failed to update VendorTerm: vendorId=${vendorId}, termPeriodTypeId=${termPeriodTypeId}, locationId=${locationId}`);
        }
        return response.data;
    }
    /**
     * Update a vendor term with full response
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @param data - Updated vendor term data
     * @returns ResponseData with updated vendor term
     */
    updateRaw(vendorId, termPeriodTypeId, locationId, data) {
        const url = this.buildUrl(`${vendorId}/${termPeriodTypeId}/${locationId}`);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a vendor term
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @returns True if deleted successfully
     */
    async delete(vendorId, termPeriodTypeId, locationId) {
        const response = await this.deleteRaw(vendorId, termPeriodTypeId, locationId);
        return response.success;
    }
    /**
     * Delete a vendor term with full response
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(vendorId, termPeriodTypeId, locationId) {
        const url = this.buildUrl(`${vendorId}/${termPeriodTypeId}/${locationId}`);
        return this.http.delete(url);
    }
    // ==================== CUSTOM QUERIES ====================
    /**
     * Get all terms for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor term views
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all terms for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor term views
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
    // ==================== MODEL VIEW QUERIES ====================
    /**
     * Get all vendor terms as model views
     * @returns Array of vendor term model views
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor terms as model views with full response
     * @returns ResponseData with array of vendor term model views
     */
    getViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get vendor term model view by triple composite key
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @returns The vendor term model view
     */
    async getView(vendorId, termPeriodTypeId, locationId) {
        const response = await this.getViewRaw(vendorId, termPeriodTypeId, locationId);
        if (!response.data) {
            throw new Error(`VendorTerm view not found: vendorId=${vendorId}, termPeriodTypeId=${termPeriodTypeId}, locationId=${locationId}`);
        }
        return response.data;
    }
    /**
     * Get vendor term model view by triple composite key with full response
     * @param vendorId - Vendor ID
     * @param termPeriodTypeId - Term period type ID
     * @param locationId - Location ID
     * @returns ResponseData with vendor term model view
     */
    getViewRaw(vendorId, termPeriodTypeId, locationId) {
        const url = this.buildUrl(`${vendorId}/${termPeriodTypeId}/${locationId}/view`);
        return this.http.get(url);
    }
    /**
     * Get all term model views for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor term model views
     */
    async getViewsByVendor(vendorId) {
        const response = await this.getViewsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all term model views for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor term model views
     */
    getViewsByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}/views`);
        return this.http.get(url);
    }
}
VendorTermService.DEFAULT_SERVICE_PATH = 'vendors';
VendorTermService.BASE_PATH = 'api/vendor-terms';
//# sourceMappingURL=VendorTermService.js.map